// Shared constants and helpers for Shop components
import dayjs from "dayjs";

export const API_ORIGIN =
  process.env.NODE_ENV === "development" ? "http://localhost:5001" : "";

export const resolveProductImageUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url}`;
};

const HEX_COLOR_RE = /^#[0-9a-fA-F]{3}([0-9a-fA-F]{3})?$/;

const GERMAN_COLOR_NAME_TO_HEX = {
  blau: "#2563eb",
  hellblau: "#60a5fa",
  dunkelblau: "#1e3a8a",
  rot: "#dc2626",
  dunkelrot: "#991b1b",
  gruen: "#16a34a",
  dunkelgruen: "#166534",
  hellgruen: "#4ade80",
  gelb: "#facc15",
  orange: "#f97316",
  lila: "#7c3aed",
  violett: "#8b5cf6",
  rosa: "#ec4899",
  pink: "#ec4899",
  schwarz: "#111827",
  weiss: "#ffffff",
  grau: "#6b7280",
  silber: "#c0c0c0",
  gold: "#d4af37",
  braun: "#8b5a2b",
  beige: "#d6c4a1",
  tuerkis: "#14b8a6",
  petrol: "#0f766e",
  mint: "#6ee7b7",
};

const normalizeColorKeyword = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[\s_-]+/g, "");

const normalizeHexColor = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "";

  const withHash = raw.startsWith("#") ? raw : `#${raw}`;
  if (!HEX_COLOR_RE.test(withHash)) return "";

  if (withHash.length === 4) {
    return `#${withHash[1]}${withHash[1]}${withHash[2]}${withHash[2]}${withHash[3]}${withHash[3]}`.toLowerCase();
  }

  return withHash.toLowerCase();
};

export const resolveColorInputToHex = (value) => {
  const normalizedHex = normalizeHexColor(value);
  if (normalizedHex) return normalizedHex;

  const keyword = normalizeColorKeyword(value);
  if (!keyword) return "";

  return GERMAN_COLOR_NAME_TO_HEX[keyword] || "";
};

export const ORDER_STATUS_OPTIONS = [
  "created",
  "processing",
  "approved",
  "exportedSepa",
  "goods_received",
  "fulfilled",
  "canceled",
];

export const ORDER_STATUS_META = {
  created: {
    label: "Neu",
    color: "info",
    tooltip: "Bestellung eingegangen, noch nicht geprüft",
  },
  processing: {
    label: "In Bearbeitung",
    color: "warning",
    tooltip: "Bestellung wird aktuell bearbeitet",
  },
  approved: {
    label: "Freigegeben",
    color: "warning",
    tooltip: "Bestellung geprüft und freigegeben",
  },
  exportedSepa: {
    label: "SEPA exportiert",
    color: "secondary",
    tooltip: "SEPA-XML wurde erstellt, Lastschrift wird eingezogen",
  },
  goods_received: {
    label: "Ware erhalten",
    color: "secondary",
    tooltip: "Ware beim Kunden angekommen – Zahlung ggf. noch ausstehend",
  },
  fulfilled: {
    label: "Erfüllt",
    color: "success",
    tooltip: "Zahlung eingegangen, Bestellung abgeschlossen",
  },
  canceled: {
    label: "Storniert",
    color: "error",
    tooltip: "Bestellung wurde storniert",
  },
};

export const ORDER_STATUSES_EXPORTABLE = new Set([
  "created",
  "processing",
  "approved",
]);

export const FULFILLMENT_TYPE_OPTIONS = ["shipping", "local", "none"];

export const FULFILLMENT_TYPE_META = {
  shipping: { label: "Versand", icon: "mdi:truck-delivery-outline" },
  local: { label: "Vor Ort", icon: "mdi:hand-extended-outline" },
  none: { label: "Keine", icon: "mdi:minus-circle-outline" },
};

export const FULFILLMENT_STATUS_META = {
  new: { label: "Neu", color: "info" },
  picking: { label: "Kommissionierung", color: "warning" },
  packed: { label: "Verpackt", color: "warning" },
  shipped: { label: "Versandt", color: "secondary" },
  delivered: { label: "Zugestellt", color: "success" },
  handed_out: { label: "Ausgegeben", color: "success" },
  issue: { label: "Problem", color: "error" },
};

export const FULFILLMENT_STATUS_OPTIONS = Object.keys(FULFILLMENT_STATUS_META);

export const getFulfillmentStatusMeta = (status) =>
  FULFILLMENT_STATUS_META[status] || {
    label: String(status || "").trim(),
    color: "default",
  };

export const PROMOTION_DISPLAY_MODES = {
  COUNTDOWN: "countdown",
  DATE: "date",
  SHORT: "short",
};

const PROMOTION_DISPLAY_MODE_SET = new Set(
  Object.values(PROMOTION_DISPLAY_MODES)
);

export const EMPTY_ARRAY = [];

export const CART_STORAGE_KEY = "ma-shop-cart";

const PRODUCT_CATEGORY_COLLATOR = new Intl.Collator("de-DE", {
  sensitivity: "base",
  numeric: true,
});
const PRODUCT_CATEGORY_SPLIT_RE = /[;,\n]+/;

export const normalizeProductCategory = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

export const getProductCategoryKey = (value) =>
  normalizeProductCategory(value).toLocaleLowerCase("de-DE");

export const normalizeProductCategories = (value) => {
  const rawValues = Array.isArray(value) ? value : [value];
  const categoriesByKey = new Map();

  rawValues.forEach((entry) => {
    String(entry || "")
      .split(PRODUCT_CATEGORY_SPLIT_RE)
      .map((part) => normalizeProductCategory(part))
      .filter(Boolean)
      .forEach((category) => {
        const categoryKey = getProductCategoryKey(category);
        if (!categoryKey || categoriesByKey.has(categoryKey)) return;
        categoriesByKey.set(categoryKey, category);
      });
  });

  return Array.from(categoriesByKey.values());
};

export const getProductCategoryLabels = (productOrCategories) => {
  if (Array.isArray(productOrCategories)) {
    return normalizeProductCategories(productOrCategories);
  }

  if (typeof productOrCategories === "string") {
    return normalizeProductCategories(productOrCategories);
  }

  if (
    Array.isArray(productOrCategories?.categories) &&
    productOrCategories.categories.length
  ) {
    return normalizeProductCategories(productOrCategories.categories);
  }

  return normalizeProductCategories(productOrCategories?.category);
};

export const getProductCategoryLabel = (productOrCategory) => {
  return getProductCategoryLabels(productOrCategory)[0] || "";
};

export const getUniqueProductCategories = (products = []) => {
  const categoriesByKey = new Map();

  (Array.isArray(products) ? products : []).forEach((product) => {
    getProductCategoryLabels(product).forEach((category) => {
      const categoryKey = getProductCategoryKey(category);
      if (!categoryKey || categoriesByKey.has(categoryKey)) return;
      categoriesByKey.set(categoryKey, category);
    });
  });

  return Array.from(categoriesByKey.values()).sort((left, right) =>
    PRODUCT_CATEGORY_COLLATOR.compare(left, right)
  );
};

export const getVariantStock = (variant) => {
  const rawStock = variant?.stock;
  if (rawStock === null || typeof rawStock === "undefined" || rawStock === "") {
    return null;
  }

  const numericStock = Number(rawStock);
  if (!Number.isInteger(numericStock) || numericStock < 0) {
    return null;
  }

  return numericStock;
};

export const isVariantInStock = (variant) => {
  if (typeof variant?.isInStock === "boolean") {
    return variant.isInStock;
  }

  const stock = getVariantStock(variant);
  if (stock === null) return true;
  return stock > 0;
};

export const getVariantBySelection = (product, selectedValues = {}) => {
  const optionSets = Array.isArray(product?.optionSets)
    ? product.optionSets
    : [];
  const activeVariants = (product?.variants || []).filter(
    (variant) => variant?.isActive !== false
  );

  if (!optionSets.length) {
    return (
      activeVariants.find((variant) => isVariantInStock(variant)) ||
      activeVariants[0] ||
      product?.variants?.[0] || {
        key: "default",
        label: "Standard",
        price: product?.basePrice || 0,
        optionValues: [],
      }
    );
  }

  const key = optionSets
    .map(
      (set) =>
        `${set.name}:${selectedValues?.[set.name] || set.values?.[0] || ""}`
    )
    .join("|");

  const selectedVariant = activeVariants.find(
    (variant) => variant?.key === key
  );
  if (selectedVariant) return selectedVariant;

  return (
    activeVariants.find((variant) => isVariantInStock(variant)) ||
    activeVariants[0]
  );
};

export const normalizeOptionSetsFromForm = (optionSets) => {
  if (!Array.isArray(optionSets)) return [];

  return optionSets
    .map((set) => {
      const name = String(set?.name || "").trim();
      const values = String(set?.valuesText || "")
        .split(",")
        .map((value) => value.trim())
        .filter(Boolean);

      if (!name || values.length === 0) return null;

      const isColorSet = Boolean(set?.isColorSet);
      const rawColorMap =
        set?.colorMap && typeof set.colorMap === "object" ? set.colorMap : {};

      const colorMap = {};
      values.forEach((value) => {
        const resolvedHex = resolveColorInputToHex(rawColorMap[value]);
        if (resolvedHex) {
          colorMap[value] = resolvedHex;
        }
      });

      return { name, values, isColorSet, colorMap };
    })
    .filter(Boolean);
};

export const isValidHexColor = (value) =>
  HEX_COLOR_RE.test(String(value || ""));

const normalizePromotionDisplayMode = (value) => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return PROMOTION_DISPLAY_MODE_SET.has(normalized)
    ? normalized
    : PROMOTION_DISPLAY_MODES.COUNTDOWN;
};

const toValidDate = (value) => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

export const getActivePromotion = (product, referenceDate = new Date()) => {
  const rawPromotion =
    product?.promotion && typeof product.promotion === "object"
      ? product.promotion
      : product?.activePromotion && typeof product.activePromotion === "object"
      ? product.activePromotion
      : null;

  if (!rawPromotion || !rawPromotion.isEnabled) return null;

  const discountPercent = Number(rawPromotion.discountPercent);
  if (
    !Number.isFinite(discountPercent) ||
    discountPercent <= 0 ||
    discountPercent > 100
  ) {
    return null;
  }

  const validUntilDate = toValidDate(rawPromotion.validUntil);
  if (!validUntilDate || validUntilDate.getTime() <= referenceDate.getTime()) {
    return null;
  }

  return {
    isEnabled: true,
    discountPercent: Math.round((discountPercent + Number.EPSILON) * 100) / 100,
    validUntil: validUntilDate,
    displayMode: normalizePromotionDisplayMode(rawPromotion.displayMode),
  };
};

export const applyPromotionDiscount = (price, promotion) => {
  const numericPrice = Number(price || 0);
  if (!Number.isFinite(numericPrice)) return 0;
  if (!promotion) return numericPrice;
  const discounted =
    numericPrice *
    Math.max(0, 1 - Number(promotion.discountPercent || 0) / 100);
  return Math.round((discounted + Number.EPSILON) * 100) / 100;
};

export const getPromotionCountdown = (
  promotion,
  referenceDate = new Date()
) => {
  if (!promotion?.validUntil) return null;
  const validUntilDate = toValidDate(promotion.validUntil);
  if (!validUntilDate) return null;

  const diffMs = validUntilDate.getTime() - referenceDate.getTime();
  if (diffMs <= 0) return null;

  const totalSeconds = Math.floor(diffMs / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return {
    totalSeconds,
    days,
    hours,
    minutes,
    seconds,
  };
};

export const formatPromotionValidUntil = (value) => {
  const date = toValidDate(value);
  if (!date) return "";
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export const getPromotionHintText = (promotion, referenceDate = new Date()) => {
  if (!promotion) return "";

  if (promotion.displayMode === PROMOTION_DISPLAY_MODES.SHORT) {
    return "Nur für kurze Zeit!";
  }

  if (promotion.displayMode === PROMOTION_DISPLAY_MODES.DATE) {
    const formattedUntil = formatPromotionValidUntil(promotion.validUntil);
    return formattedUntil
      ? `Nur gültig bis ${formattedUntil}`
      : "Nur für kurze Zeit!";
  }

  const countdown = getPromotionCountdown(promotion, referenceDate);
  if (!countdown) return "Nur für kurze Zeit!";
  return `Nur noch ${countdown.days} Tage, ${countdown.hours} Stunden, ${countdown.minutes} Minuten, ${countdown.seconds} Sekunden`;
};

/** Returns { min, max } price from a product's active variants (gross if taxRate set). */
export const getPriceRange = (product, options = {}) => {
  const referenceDate = options.referenceDate || new Date();
  const promotion =
    options.promotion ||
    (options.applyPromotion
      ? getActivePromotion(product, referenceDate)
      : null);
  const taxRate = Number(product?.taxRate || 0);
  const activeVariants = (product?.variants || []).filter(
    (v) => v.isActive !== false
  );
  if (!activeVariants.length) {
    const p = Number(product?.basePrice || 0);
    const discounted = promotion ? applyPromotionDiscount(p, promotion) : p;
    return {
      min: computeGrossPrice(discounted, taxRate),
      max: computeGrossPrice(discounted, taxRate),
    };
  }

  const prices = activeVariants.map((v) => {
    const basePrice = Number(v.price ?? product?.basePrice ?? 0);
    const net = promotion
      ? applyPromotionDiscount(basePrice, promotion)
      : basePrice;
    return computeGrossPrice(net, taxRate);
  });
  return { min: Math.min(...prices), max: Math.max(...prices) };
};

export const toCurrency = (value) =>
  Number(value || 0).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/** Compute gross price from net price and tax rate (percentage). */
export const computeGrossPrice = (netPrice, taxRate) => {
  const net = Number(netPrice || 0);
  const rate = Number(taxRate || 0);
  if (!Number.isFinite(net) || !Number.isFinite(rate) || rate <= 0) return net;
  return Math.round((net * (1 + rate / 100) + Number.EPSILON) * 100) / 100;
};

/** Format tax hint text for display, e.g. "inkl. 19% MwSt." */
export const formatTaxHint = (taxRate) => {
  const rate = Number(taxRate || 0);
  if (!Number.isFinite(rate) || rate <= 0) return "";
  const formatted =
    rate % 1 === 0 ? String(rate) : rate.toLocaleString("de-DE");
  return `inkl. ${formatted}% MwSt.`;
};

export const getStatusMeta = (status) =>
  ORDER_STATUS_META[status] || {
    label: String(status || "").trim(),
    color: "default",
    tooltip: "",
  };

// ─── Orders tab constants ────────────────────────────────────────────────────

export const OPTIONAL_TABLE_COLUMNS = [
  { key: "email", label: "E-Mail" },
  { key: "address", label: "Adresse" },
  { key: "orderId", label: "Bestell-ID" },
];

export const OPTIONAL_TABLE_COLUMN_LABELS = OPTIONAL_TABLE_COLUMNS.reduce(
  (acc, option) => ({ ...acc, [option.key]: option.label }),
  {},
);

export const PDF_FIELD_OPTIONS = [
  { key: "date", label: "Datum" },
  { key: "status", label: "Status" },
  { key: "member", label: "Mitglied" },
  { key: "email", label: "E-Mail" },
  { key: "address", label: "Adresse" },
  { key: "items", label: "Positionen" },
  { key: "total", label: "Gesamt" },
  { key: "orderId", label: "Bestell-ID" },
];

export const PDF_FIELD_LABELS = PDF_FIELD_OPTIONS.reduce(
  (acc, option) => ({ ...acc, [option.key]: option.label }),
  {},
);

export const ORDER_SORT_OPTIONS = [
  { value: "dateDesc", label: "Datum: Neu nach Alt" },
  { value: "dateAsc", label: "Datum: Alt nach Neu" },
  { value: "totalDesc", label: "Gesamt: Hoch nach Niedrig" },
  { value: "totalAsc", label: "Gesamt: Niedrig nach Hoch" },
  { value: "memberAsc", label: "Mitglied: A-Z" },
  { value: "memberDesc", label: "Mitglied: Z-A" },
  { value: "emailAsc", label: "E-Mail: A-Z" },
  { value: "emailDesc", label: "E-Mail: Z-A" },
  { value: "addressAsc", label: "Adresse: A-Z" },
  { value: "addressDesc", label: "Adresse: Z-A" },
  { value: "itemsAsc", label: "Positionen: A-Z" },
  { value: "itemsDesc", label: "Positionen: Z-A" },
  { value: "orderIdAsc", label: "Bestell-ID: A-Z" },
  { value: "orderIdDesc", label: "Bestell-ID: Z-A" },
  { value: "statusAsc", label: "Status: A-Z" },
];

export const ORDER_HEADER_SORT_CONFIG = {
  member: { asc: "memberAsc", desc: "memberDesc", defaultDirection: "asc" },
  email: { asc: "emailAsc", desc: "emailDesc", defaultDirection: "asc" },
  address: { asc: "addressAsc", desc: "addressDesc", defaultDirection: "asc" },
  date: { asc: "dateAsc", desc: "dateDesc", defaultDirection: "desc" },
  items: { asc: "itemsAsc", desc: "itemsDesc", defaultDirection: "asc" },
  total: { asc: "totalAsc", desc: "totalDesc", defaultDirection: "desc" },
  orderId: { asc: "orderIdAsc", desc: "orderIdDesc", defaultDirection: "asc" },
};

export const PROCESSING_SCOPE_OPTIONS = [
  { value: "visible", label: "Sichtbare Bestellungen" },
  { value: "selected", label: "Ausgewählte Bestellungen" },
  { value: "all", label: "Alle geladenen Bestellungen" },
];

export const ACTIVE_PREPARATION_STATUSES = new Set([
  "created",
  "processing",
  "approved",
]);

export const DEFAULT_PDF_FIELDS = ["date", "status", "member", "items", "total"];
export const PREPARATION_SORT_DEFAULT = { field: "quantity", direction: "desc" };
export const ORDER_STATUSES_EDITABLE = new Set(["created", "processing"]);

// ─── Orders tab utility functions ────────────────────────────────────────────

export const normalizeSearchText = (value) =>
  String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();

export const toArrayValue = (value) =>
  Array.isArray(value)
    ? value
    : String(value || "")
        .split(",")
        .filter(Boolean);

export const sanitizeFilename = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "");

export const compareGerman = (left, right) =>
  String(left || "").localeCompare(String(right || ""), "de", {
    sensitivity: "base",
  });

export const getPreparationItemKey = (item) =>
  `${item.productName || "Produkt"}::${item.variantLabel || "Standard"}`;

export const getBuyerContract = (order) => {
  const contract = order?.buyerContractId;
  return contract && typeof contract === "object" ? contract : null;
};

export const getContractName = (order) => {
  const contract = getBuyerContract(order);
  if (!contract) return "Unbekannter Vertrag";
  return (
    contract.owner ||
    `${contract.firstName || ""} ${contract.lastName || ""}`.trim() ||
    "Unbekannter Vertrag"
  );
};

export const getContractEmail = (order) => {
  const contract = getBuyerContract(order);
  const email = String(contract?.email || "").trim();
  return email || "\u2014";
};

export const getContractAddress = (order) => {
  const snap = order?.shippingSnapshot;
  if (snap?.street || snap?.city) {
    const street = String(snap.street || "").trim();
    const cityLine = [snap.zip, snap.city]
      .map((part) => String(part || "").trim())
      .filter(Boolean)
      .join(" ");
    return [street, cityLine].filter(Boolean).join(", ") || "\u2014";
  }

  const contract = getBuyerContract(order);
  if (!contract) return "\u2014";

  const street = String(contract.street || "").trim();
  const cityLine = [contract.zip, contract.city]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" ");

  return [street, cityLine].filter(Boolean).join(", ") || "\u2014";
};

export const formatMonthFilterLabel = (monthValue) => {
  if (!monthValue) return "Alle";
  const parsed = dayjs(`${monthValue}-01`);
  if (!parsed.isValid()) return monthValue;
  return parsed.format("MM.YYYY");
};

export const getProcessingScopeLabel = (scope) =>
  PROCESSING_SCOPE_OPTIONS.find((option) => option.value === scope)?.label ||
  PROCESSING_SCOPE_OPTIONS[0].label;

export const buildCompactSummary = (lineItems) => {
  if (!Array.isArray(lineItems) || !lineItems.length) return "\u2014";
  return lineItems
    .map((lineItem) => {
      const variantPart =
        lineItem.variantLabel && lineItem.variantLabel !== "Standard"
          ? ` (${lineItem.variantLabel})`
          : "";
      return `${lineItem.productName}${variantPart} \u00d7${lineItem.quantity}`;
    })
    .join(", ");
};

export const buildOrderSearchIndex = (order) => {
  const lineItemText = (order?.lineItems || [])
    .map((lineItem) => {
      const variantOptions = (lineItem?.variantOptions || [])
        .map((option) => `${option.name} ${option.value}`)
        .join(" ");
      return `${lineItem.productName} ${lineItem.variantLabel} ${variantOptions}`;
    })
    .join(" ");

  return normalizeSearchText(
    [
      order?._id,
      getContractName(order),
      getContractEmail(order),
      getContractAddress(order),
      getStatusMeta(order?.status).label,
      lineItemText,
    ]
      .filter(Boolean)
      .join(" "),
  );
};

export const sortOrders = (orders, sortBy) => {
  const next = [...orders];

  next.sort((a, b) => {
    if (sortBy === "dateAsc") {
      return dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf();
    }
    if (sortBy === "totalDesc") {
      return (
        Number(b?.totals?.grandTotal || 0) - Number(a?.totals?.grandTotal || 0)
      );
    }
    if (sortBy === "totalAsc") {
      return (
        Number(a?.totals?.grandTotal || 0) - Number(b?.totals?.grandTotal || 0)
      );
    }
    if (sortBy === "memberAsc") {
      return compareGerman(getContractName(a), getContractName(b));
    }
    if (sortBy === "memberDesc") {
      return compareGerman(getContractName(b), getContractName(a));
    }
    if (sortBy === "emailAsc") {
      return compareGerman(getContractEmail(a), getContractEmail(b));
    }
    if (sortBy === "emailDesc") {
      return compareGerman(getContractEmail(b), getContractEmail(a));
    }
    if (sortBy === "addressAsc") {
      return compareGerman(getContractAddress(a), getContractAddress(b));
    }
    if (sortBy === "addressDesc") {
      return compareGerman(getContractAddress(b), getContractAddress(a));
    }
    if (sortBy === "itemsAsc") {
      return compareGerman(
        buildCompactSummary(a.lineItems),
        buildCompactSummary(b.lineItems),
      );
    }
    if (sortBy === "itemsDesc") {
      return compareGerman(
        buildCompactSummary(b.lineItems),
        buildCompactSummary(a.lineItems),
      );
    }
    if (sortBy === "orderIdAsc") {
      return compareGerman(a?._id, b?._id);
    }
    if (sortBy === "orderIdDesc") {
      return compareGerman(b?._id, a?._id);
    }
    if (sortBy === "statusAsc") {
      return compareGerman(
        getStatusMeta(a.status).label,
        getStatusMeta(b.status).label,
      );
    }
    return dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf();
  });

  return next;
};

export const aggregateOrderItems = (orders) => {
  const groupedItems = new Map();
  let totalQuantity = 0;
  let totalRevenue = 0;

  (orders || []).forEach((order) => {
    (order?.lineItems || []).forEach((lineItem) => {
      const key = `${lineItem.productId || lineItem.productName}::${
        lineItem.variantKey || lineItem.variantLabel || "Standard"
      }`;
      const existing = groupedItems.get(key) || {
        productName: lineItem.productName,
        variantLabel: lineItem.variantLabel || "Standard",
        quantity: 0,
        revenue: 0,
        orderIds: new Set(),
      };

      const quantity = Number(lineItem.quantity || 0);
      const lineTotal = Number(lineItem.lineTotal || 0);

      existing.quantity += quantity;
      existing.revenue += lineTotal;
      existing.orderIds.add(String(order._id || ""));

      groupedItems.set(key, existing);
      totalQuantity += quantity;
      totalRevenue += lineTotal;
    });
  });

  const items = Array.from(groupedItems.values()).map((entry) => ({
    productName: entry.productName,
    variantLabel: entry.variantLabel,
    quantity: entry.quantity,
    orderCount: entry.orderIds.size,
    revenue: entry.revenue,
  }));

  return {
    items,
    totalQuantity,
    totalRevenue: Math.round((totalRevenue + Number.EPSILON) * 100) / 100,
  };
};

export const resolvePreparationSortValue = (item, field) => {
  if (field === "productName") return String(item.productName || "");
  if (field === "variantLabel") return String(item.variantLabel || "");
  if (field === "quantity") return Number(item.quantity || 0);
  if (field === "orderCount") return Number(item.orderCount || 0);
  if (field === "revenue") return Number(item.revenue || 0);
  return "";
};

export const sortPreparationItems = (items, sortConfig) => {
  const next = [...(items || [])];
  const { field, direction } = sortConfig || PREPARATION_SORT_DEFAULT;

  next.sort((a, b) => {
    const left = resolvePreparationSortValue(a, field);
    const right = resolvePreparationSortValue(b, field);

    let baseResult;
    if (typeof left === "number" && typeof right === "number") {
      baseResult = left - right;
    } else {
      baseResult = compareGerman(left, right);
    }

    if (baseResult === 0) {
      baseResult = compareGerman(
        getPreparationItemKey(a),
        getPreparationItemKey(b),
      );
    }

    return direction === "desc" ? -baseResult : baseResult;
  });

  return next;
};
